/**
 * Lekka normalizacja i walidacja inputu adresowego dla autocomplete.
 * Celem jest częstsze trafianie do podpowiedzi mimo drobnych literówek
 * (zbędne spacje, "ul.", "5 a" zamiast "5a", "5 / 3" zamiast "5/3").
 */

import { STREET_PREFIX_RE } from "@/lib/geoText";

/**
 * Zwraca tekst wejściowy znormalizowany pod wyszukiwanie:
 * - usuwa wielokrotne białe znaki,
 * - obcina prefiks "ul.", "ulica", "al.", "plac", "os.",
 * - skleja numer domu: "5 a" -> "5a", "5 / 3" -> "5/3",
 * - usuwa spacje wokół ukośnika w numerze.
 *
 * Funkcja jest bezpieczna dla pustego stringa i nigdy nie rzuca.
 */
export const normalizeAddressInput = (raw: string): string => {
  if (!raw) return "";
  let s = raw.replace(/\s+/g, " ").trim();
  s = s.replace(STREET_PREFIX_RE, "");
  // "5 a" / "12 B" -> "5a" / "12B" (cyfra + spacja + pojedyncza litera na końcu lub przed /)
  s = s.replace(/(\d)\s+([a-zA-Z])(?=\b|\/)/g, "$1$2");
  // "5 / 3" -> "5/3"
  s = s.replace(/\s*\/\s*/g, "/");
  return s;
};

export interface ParsedAddress {
  street: string;
  houseNumber: string | null;
  /** Tekst używany do wyszukiwania (street, bez numeru). */
  searchTerm: string;
  /** Wskazówka do pokazania użytkownikowi gdy parser rozpoznał numer. */
  hint: string | null;
}

const HOUSE_NUMBER_TAIL_RE = /^(.+?)\s+(\d+[a-zA-Z]?(?:\/\d+[a-zA-Z]?)?)$/;

/**
 * Rozdziela "Marszałkowska 15" -> { street, houseNumber }.
 * Działa na już znormalizowanym tekście.
 */
export const parseAddressInput = (raw: string): ParsedAddress => {
  const normalized = normalizeAddressInput(raw);
  const match = normalized.match(HOUSE_NUMBER_TAIL_RE);
  if (match) {
    const street = match[1].trim();
    const houseNumber = match[2];
    return {
      street,
      houseNumber,
      searchTerm: street,
      hint: `Szukam: ulica „${street}", numer ${houseNumber}.`,
    };
  }
  return {
    street: normalized,
    houseNumber: null,
    searchTerm: normalized,
    hint: null,
  };
};

const CITY_INVALID_RE = /[0-9@#$%^&*_=+<>{}[\]\\|]/;

export interface CityValidation {
  valid: boolean;
  /** Komunikat ostrzegawczy gdy input jest podejrzany (np. cyfry). */
  warning: string | null;
}

/**
 * Lekka walidacja inputu miasta - sygnalizuje typowe pomyłki, np. cyfry,
 * znaki specjalne. Nie blokuje wyszukiwania, tylko pomaga pokazać hint.
 */
export const validateCityInput = (raw: string): CityValidation => {
  const v = (raw || "").trim();
  if (!v) return { valid: true, warning: null };
  if (CITY_INVALID_RE.test(v)) {
    return {
      valid: false,
      warning: "Nazwa miasta nie powinna zawierać cyfr ani znaków specjalnych.",
    };
  }
  return { valid: true, warning: null };
};
